import React, { useState, useRef, useEffect } from 'react';
import { Button, colors } from '@dhis2/ui';

const RichTextEditor = ({ value, onChange, placeholder, rows = 4, disabled }) => {
  const editorRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  const handleInput = () => {
    if (editorRef.current && onChange) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current.focus();
    handleInput();
  };

  const formatText = (style) => {
    switch (style) {
      case 'bold':
        execCommand('bold');
        break;
      case 'italic':
        execCommand('italic');
        break;
      case 'underline':
        execCommand('underline');
        break;
      case 'bulletList':
        execCommand('insertUnorderedList');
        break;
      case 'numberedList':
        execCommand('insertOrderedList');
        break;
      case 'indent':
        execCommand('indent');
        break;
      case 'outdent':
        execCommand('outdent');
        break;
      default:
        break;
    }
  };

  const insertBulletPoint = () => {
    execCommand('insertUnorderedList');
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  };

  if (disabled) {
    return (
      <div
        style={{
          padding: '10px',
          border: `1px solid ${colors.grey300}`,
          borderRadius: '4px',
          backgroundColor: '#f5f5f5',
          minHeight: '80px',
          fontSize: '0.9rem',
          lineHeight: '1.5',
          color: '#666'
        }}
        dangerouslySetInnerHTML={{ __html: value || placeholder || 'No content' }}
      />
    );
  }

  return (
    <div style={{ border: `1px solid ${colors.grey300}`, borderRadius: '4px', overflow: 'hidden' }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '4px',
        padding: '8px',
        backgroundColor: '#f8f9fa',
        borderBottom: `1px solid ${colors.grey300}`,
        position: 'sticky',
        top: 0,
        zIndex: 1
      }}>
        <button
          type="button"
          onClick={() => formatText('bold')}
          style={{
            padding: '4px 8px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            backgroundColor: 'white',
            cursor: 'pointer',
            fontWeight: 'bold'
          }}
          title="Bold"
        >
          <strong>B</strong>
        </button>
        <button
          type="button"
          onClick={() => formatText('italic')}
          style={{
            padding: '4px 8px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            backgroundColor: 'white',
            cursor: 'pointer',
            fontStyle: 'italic'
          }}
          title="Italic"
        >
          <em>I</em>
        </button>
        <button
          type="button"
          onClick={() => formatText('underline')}
          style={{
            padding: '4px 8px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            backgroundColor: 'white',
            cursor: 'pointer',
            textDecoration: 'underline'
          }}
          title="Underline"
        >
          <u>U</u>
        </button>
        <div style={{ width: '1px', backgroundColor: '#ddd', margin: '0 4px' }} />
        <button
          type="button"
          onClick={() => formatText('bulletList')}
          style={{
            padding: '4px 8px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            backgroundColor: 'white',
            cursor: 'pointer'
          }}
          title="Bullet List"
        >
          • List
        </button>
        <button
          type="button"
          onClick={() => formatText('numberedList')}
          style={{
            padding: '4px 8px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            backgroundColor: 'white',
            cursor: 'pointer'
          }}
          title="Numbered List"
        >
          1. List
        </button>
        <div style={{ width: '1px', backgroundColor: '#ddd', margin: '0 4px' }} />
        <button
          type="button"
          onClick={() => formatText('outdent')}
          style={{
            padding: '4px 8px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            backgroundColor: 'white',
            cursor: 'pointer'
          }}
          title="Outdent"
        >
          ← Indent
        </button>
        <button
          type="button"
          onClick={() => formatText('indent')}
          style={{
            padding: '4px 8px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            backgroundColor: 'white',
            cursor: 'pointer'
          }}
          title="Indent"
        >
          Indent →
        </button>
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable={!disabled}
        onInput={handleInput}
        onPaste={handlePaste}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        style={{
          padding: '10px',
          minHeight: `${rows * 20}px`,
          maxHeight: '300px',
          overflowY: 'auto',
          fontSize: '0.9rem',
          lineHeight: '1.5',
          backgroundColor: isFocused ? '#fff' : '#fafafa',
          outline: 'none',
          cursor: disabled ? 'not-allowed' : 'text'
        }}
        placeholder={placeholder}
      />
      
      {/* Hint */}
      <div style={{
        padding: '4px 8px',
        fontSize: '0.7rem',
        color: '#999',
        backgroundColor: '#f8f9fa',
        borderTop: `1px solid ${colors.grey300}`
      }}>
        💡 Tip: Use the toolbar to format text with bullet points, numbered lists, bold, etc.
      </div>
    </div>
  );
};

export default RichTextEditor;